// The dashboard charts (recharts). Colours from the validated reference palette: series 1 blue,
// series 2 orange; profit/loss as the diverging blue <-> red pair. Thin marks, solid hairline grid,
// a legend for two series, a tooltip on every chart, text in ink colours (never the series colour).
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const C = { s1: "#2a78d6", s2: "#eb6834", pos: "#2a78d6", neg: "#e34948", grid: "#e7e6e2", ink: "#52514e", surface: "#ffffff" };
const compact = (v) => {
  const a = Math.abs(v);
  return a >= 1e6 ? `${(v / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M` : a >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : `${v}`;
};
const full = (v) => Number(v || 0).toLocaleString("en-KE", { maximumFractionDigits: 0 });
const tick = { fill: C.ink, fontSize: 12 };

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm">
      <div className="mb-1 font-semibold text-slate-900">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-slate-700">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: p.color || p.payload?.fill }} />
          <span>{p.name}</span><span className="ml-auto pl-3 tabular-nums">KES {full(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

/** Earned vs all costs, month by month (two series on one KES scale). */
export function EarnedVsCosts({ months }) {
  const data = (months || []).map((m) => ({ month: m.month, earned: m.revenue, costs: m.fuel + m.other }));
  if (data.length < 2) return <p className="mt-3 text-sm text-slate-500">The chart appears once there are two months to compare.</p>;
  return (
    <div className="mt-3 h-64" role="img" aria-label="Earned and costs by month">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 24, left: 4, bottom: 0 }}>
          <CartesianGrid stroke={C.grid} vertical={false} />
          <XAxis dataKey="month" tick={tick} tickLine={false} axisLine={{ stroke: C.grid }} />
          <YAxis tickFormatter={compact} tick={tick} tickLine={false} axisLine={false} width={48} />
          <Tooltip content={<Tip />} cursor={{ stroke: C.grid, strokeWidth: 1 }} />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: C.ink }} />
          <Line type="monotone" dataKey="earned" name="Earned" stroke={C.s1} strokeWidth={2} dot={{ r: 4, fill: C.s1, stroke: C.surface, strokeWidth: 2 }} activeDot={{ r: 6 }} />
          <Line type="monotone" dataKey="costs" name="Costs (fuel + other)" stroke={C.s2} strokeWidth={2} dot={{ r: 4, fill: C.s2, stroke: C.surface, strokeWidth: 2 }} activeDot={{ r: 6 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// a bar with its rounded end on the data side and square at the zero line
function DataEndBar({ x, y, width, height, fill }) {
  if (!width || !height) return null;
  const w = Math.abs(width), left = width < 0 ? x + width : x, r = Math.min(4, w, height / 2);
  const toRight = width >= 0;
  const d = toRight
    ? `M${left},${y} H${left + w - r} Q${left + w},${y} ${left + w},${y + r} V${y + height - r} Q${left + w},${y + height} ${left + w - r},${y + height} H${left} Z`
    : `M${left + w},${y} H${left + r} Q${left},${y} ${left},${y + r} V${y + height - r} Q${left},${y + height} ${left + r},${y + height} H${left + w} Z`;
  return <path d={d} fill={fill} />;
}

/** Profit per truck: blue to the right for profit, red to the left for a loss. */
export function ProfitPerTruck({ rows }) {
  const data = (rows || []).filter((r) => r.truck_id !== "yard").map((r) => ({ plate: r.plate, profit: r.profit, fill: r.profit < 0 ? C.neg : C.pos }));
  if (!data.length) return null;
  return (
    <div className="mt-3" style={{ height: Math.max(160, data.length * 26 + 40) }} role="img" aria-label="Profit per truck">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }} barCategoryGap={6}>
          <CartesianGrid stroke={C.grid} horizontal={false} />
          <XAxis type="number" tickFormatter={compact} tick={tick} tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey="plate" tick={tick} tickLine={false} axisLine={false} width={84} />
          <ReferenceLine x={0} stroke={C.ink} strokeWidth={1} />
          <Tooltip content={<Tip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
          <Bar dataKey="profit" name="Profit" barSize={16} shape={<DataEndBar />} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-1 flex gap-4 text-xs text-slate-600">
        <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: C.pos }} />Profit</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: C.neg }} />Loss</span>
      </div>
    </div>
  );
}
